import { Link } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { useState } from "react";
import { Headphones, Radio, ExternalLink, RefreshCw } from "lucide-react";

const FixButton = ({ onFix }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try { await onFix(); } finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy} title="Restart the stream output if listeners hear buffering or skipping"
      className="rounded-lg border border-[color:var(--gold)] text-[color:var(--gold)] px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 hover:bg-[color:var(--gold)]/10 disabled:opacity-60 whitespace-nowrap"
      data-testid="studio-fix-buffering-button">
      <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> <span className="hidden sm:inline">{busy ? "Fixing..." : "Fix buffering"}</span>
    </button>
  );
};

const Badge = ({ state }) => {
  if (state?.live) return <span className="on-air-pulse rounded-md bg-[color:var(--onair)] px-3 py-1 font-mono text-xs font-bold tracking-widest whitespace-nowrap" data-testid="studio-status-badge">ON AIR / LIVE</span>;
  if (state?.now_playing) return <span className="rounded-md bg-[color:var(--green)] text-black px-3 py-1 font-mono text-xs font-bold tracking-widest whitespace-nowrap" data-testid="studio-status-badge">{state.autodj ? "AUTO DJ" : "PLAYING"}</span>;
  return <span className="rounded-md bg-white/10 px-3 py-1 font-mono text-xs font-bold tracking-widest whitespace-nowrap text-[color:var(--lr-muted)]" data-testid="studio-status-badge">SILENT</span>;
};

export const StudioHeader = ({ state, mic, onFixBuffering }) => (
  <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0B0C10]/85 backdrop-blur-xl">
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
      <Radio className="w-6 h-6 text-[color:var(--red)] shrink-0" />
      <div className="min-w-0">
        <p className="font-display text-2xl leading-none chrome-text truncate">Broadcast Studio</p>
        <p className="eyebrow !text-[0.55rem] hidden sm:block">Lowride Life Radio</p>
      </div>
      <Badge state={state} />
      <div className="hidden md:flex items-end gap-0.5 h-6 ml-2" aria-hidden data-testid="studio-header-vu">
        {Array.from({ length: 12 }).map((_, i) => (
          <span key={i} className="w-1.5 rounded-sm transition-[height] duration-75"
            style={{ height: `${mic.level * 12 > i ? 100 : 18}%`, background: i > 9 ? "var(--red)" : i > 6 ? "var(--gold)" : "var(--green)" }} />
        ))}
      </div>
      <div className="ml-auto flex items-center gap-3 sm:gap-4">
        <FixButton onFix={onFixBuffering} />
        <span className="flex items-center gap-1.5 font-mono text-sm" data-testid="studio-listener-count">
          <Headphones className="w-4 h-4 text-[color:var(--cyan)]" /> {state?.listeners ?? 0}
        </span>
        <Link to="/" target="_blank" className="hidden sm:flex items-center gap-1 text-xs font-mono text-[color:var(--lr-muted)] hover:text-white" data-testid="studio-public-link">
          Public player <ExternalLink className="w-3 h-3" />
        </Link>
        <UserButton />
      </div>
    </div>
  </header>
);
